import * as yup from 'yup';
import { Blok } from '../../interfaces/Blok.interface';
import { StringSchema } from 'yup';
import { getUrl } from '../../utils/getUrl/getUrl.util';

interface FieldBlok extends Blok {
  key: string;
  left_column_form_field?: FieldBlok[];
  right_column_form_field?: FieldBlok[];
  form_fields?: FieldBlok[];
  validation_rule?: string;
  error_message?: string;
  input_type?: string;
}

interface FilteredFields {
  [key: string]: FieldBlok;
}

// Define a type for the index signature
type SchemaMap = {
  [key: string]: yup.AnySchema | undefined;
};

const validationSchemas: SchemaMap = {
  'conveyancy-choice': yup.string().required(),
  price: yup
    .number()
    .transform((o, v) => parseFloat(v.replace(/,/g, '')))
    .typeError('Price must be a number')
    .moreThan(0, 'Price must be at least £1')
    .max(2000000, `MAX_BUY`)
    .required('Price is required'),
  tenure: yup
    .mixed()
    .oneOf(['freehold', 'leasehold'])
    .required('Property type is required'),
  postcode: yup
    .string()
    .uppercase()
    .matches(/^[A-Z]{1,2}[0-9R][0-9A-Z]? ?[0-9][A-Z]{2}$/, {
      message: 'Postcode is not valid',
      excludeEmptyString: true,
    })
    .required('Postcode is required'),
  'buying-position': yup.array(),
  title: yup
    .mixed()
    .oneOf(['Mr', 'Mrs', 'Ms', 'Miss', 'Mx', 'Dr', 'Rev', 'Prof', 'Other'])
    .required('Title is required'),
  firstName: yup.string().min(1).max(50).required('First name is required'),
  lastName: yup.string().min(1).max(50).required('Last name is required'),
  email: yup
    .string()
    .email('Must be a valid email address')
    .min(3)
    .max(50)
    .required('Email is required'),

  telephone: yup
    .string()
    .min(8, 'Telephone must be at least 8 characters')
    .max(20, 'Telephone must not be more than 20 characters')
    .matches(/^[A-Za-z 0-9\-\'\.]*$/, {
      message: 'Telephone is not valid',
      excludeEmptyString: true,
    })
    .required('Telephone is required'),
  'accept-terms-and-conditions': yup
    .bool()
    .oneOf([true], 'You must agree to the terms and conditions')
    .required(),

  buyPrice: yup
    .number()
    .transform((o, v) => parseFloat(v.replace(/,/g, '')))
    .typeError('!Price must be a number')
    .moreThan(0, 'Price must be at least £1')
    .max(2000000, `MAX_BUY`)
    .required('Price is required'),

  buyTenure: yup
    .mixed()
    .oneOf(['freehold', 'leasehold'])
    .required('Property type is required'),

  buyPostcode: yup
    .string()
    .uppercase()
    .matches(/^[A-Z]{1,2}[0-9R][0-9A-Z]? ?[0-9][A-Z]{2}$/, {
      message: 'Postcode is not valid',
      excludeEmptyString: true,
    })
    .required('Postcode is required'),

  sellPrice: yup
    .number()
    .transform((o, v) => parseFloat(v.replace(/,/g, '')))
    .typeError('Price must be a number')
    .moreThan(0, 'Price must be at least £1')
    .max(2000000, 'Price must not exceed £2,000,000')
    .required('Price is required'),

  sellTenure: yup
    .mixed()
    .oneOf(['freehold', 'leasehold'])
    .required('Property type is required'),

  sellPostcode: yup
    .string()
    .uppercase()
    .matches(/^[A-Z]{1,2}[0-9R][0-9A-Z]? ?[0-9][A-Z]{2}$/, {
      message: 'Postcode is not valid',
      excludeEmptyString: true,
    })
    .required('Postcode is required'),

  // Stage 3
  'buy-address-addressLine2': yup.string(),
  'buy-address-city': yup.string().required('City is required'),
  'buy-address-street': yup.string().required('Street is required'),
  'buy-address-postcode': yup
    .string()
    .uppercase()
    .matches(/^[A-Z]{1,2}[0-9R][0-9A-Z]? ?[0-9][A-Z]{2}$/, {
      message: 'Postcode is not valid',
      excludeEmptyString: true,
    })
    .required('Postcode is required'),

  'sell-address-addressLine2': yup
    .string()
    .when('isSellAddressSameAsCustomerAddress', (checkbox, schema) => {
      if (getUrl()?.includes('quote-stage-three') && checkbox[0]) return schema;
      return schema.required();
    }),
  'sell-address-city': yup
    .string()
    .when('isSellAddressSameAsCustomerAddress', (checkbox, schema) => {
      if (getUrl()?.includes('quote-stage-three') && checkbox[0]) return schema;
      return schema.required('City is required');
    }),
  'sell-address-street': yup
    .string()
    .when('isSellAddressSameAsCustomerAddress', (checkbox, schema) => {
      if (getUrl()?.includes('quote-stage-three') && checkbox[0]) return schema;
      return schema.required('Street is required');
    }),
  'sell-address-postcode': yup
    .string()
    .uppercase()
    .matches(/^[A-Z]{1,2}[0-9R][0-9A-Z]? ?[0-9][A-Z]{2}$/, {
      message: 'Postcode is not valid',
      excludeEmptyString: true,
    })
    .when('isSellAddressSameAsCustomerAddress', (checkbox, schema) => {
      if (getUrl()?.includes('quote-stage-three') && checkbox[0]) return schema;
      return schema.required('Postcode is required');
    }),

  'customer-address-addressLine2': yup.string(),
  'customer-address-city': yup.string().required('City is required'),
  'customer-address-street': yup.string().required('Street is required'),
  'customer-address-postcode': yup
    .string()
    .uppercase()
    .matches(/^[A-Z]{1,2}[0-9R][0-9A-Z]? ?[0-9][A-Z]{2}$/, {
      message: 'Postcode is not valid',
      excludeEmptyString: true,
    })
    .required('Postcode is required'),

  'remortgage-address-addressLine2': yup
    .string()
    .when('isRemortgageAddressSameAsCustomerAddress', (checkbox, schema) => {
      if (getUrl()?.includes('quote-stage-three') && checkbox[0]) return schema;
      return schema.required();
    }),
  'remortgage-address-city': yup
    .string()
    .when('isRemortgageAddressSameAsCustomerAddress', (checkbox, schema) => {
      if (getUrl()?.includes('quote-stage-three') && checkbox[0]) return schema;
      return schema.required('City is required');
    }),
  'remortgage-address-street': yup
    .string()
    .when('isRemortgageAddressSameAsCustomerAddress', (checkbox, schema) => {
      if (getUrl()?.includes('quote-stage-three') && checkbox[0]) return schema;
      return schema.required('Street is required');
    }),
  'remortgage-address-postcode': yup
    .string()
    .uppercase()
    .matches(/^[A-Z]{1,2}[0-9R][0-9A-Z]? ?[0-9][A-Z]{2}$/, {
      message: 'Postcode is not valid',
      excludeEmptyString: true,
    })
    .when('isRemortgageAddressSameAsCustomerAddress', (checkbox, schema) => {
      if (getUrl()?.includes('quote-stage-three') && checkbox[0]) return schema;
      return schema.required('Postcode is required');
    }),

  checked: yup
    .bool()
    .oneOf([true], 'You must agree to the terms and conditions'),
};

const getValidationSchema = (filteredFields: FilteredFields) => {
  return Object.fromEntries(
    Object.values(filteredFields)
      .map((field) => {
        const schema = validationSchemas[field.key];
        return schema ? [field.key, schema] : [];
      })
      .filter((schemaEntry) => schemaEntry.length > 0)
  );
};

export { getValidationSchema };
